import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ParsingRecoverySettings, { recoveryPolicy, recoveryError, serializeRecovery, recoveryModeLabel } from './ParsingRecoverySettings';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBase, getAuthHeaders } from '../../utils/api';

const SuperadminSettings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [parsingFeedback, setParsingFeedback] = useState({
    s3_export_enabled: true,
    github_commit_enabled: true
  });

  const [aiSettings, setAiSettings] = useState({
    preprocess_model: 'claude-haiku-5-5',  // Cheap model for classification
    parsing_model: 'claude-sonnet-5-5',  // Main parsing model
    airline_normalization_model: 'claude-haiku-5-5',  // Fast model for airline code lookup
    smart_input_model: 'claude-haiku-5-5',  // Cheap model for smart input parsing review
    parsing_recovery: recoveryPolicy(),
    // Fallback settings
    ai_model_fallback_enabled: true,
    ai_model_fallback_model: 'claude-sonnet-5-5',
    ai_model_retry_count: 3,
    ai_model_retry_backoff_ms: 1000
  });

  // Default model costs (USD per 1M tokens) - vendor list prices as of 10/2026.
  // Must mirror get_default_model_costs() in backend/models.py.
  const DEFAULT_MODEL_COSTS = [
    // Claude - current generation
    { model_id: 'claude-sonnet-5-5', input_cost_per_1m_tokens: 2.0, output_cost_per_1m_tokens: 10.0 },
    { model_id: 'claude-haiku-5-5', input_cost_per_1m_tokens: 0.10, output_cost_per_1m_tokens: 0.50 },
    { model_id: 'claude-opus-5-5', input_cost_per_1m_tokens: 4.0, output_cost_per_1m_tokens: 20.0 },
    // Claude - legacy, still served
    { model_id: 'claude-haiku-4-5-20251001', input_cost_per_1m_tokens: 1.0, output_cost_per_1m_tokens: 5.0 },
    { model_id: 'claude-sonnet-4-6', input_cost_per_1m_tokens: 3.0, output_cost_per_1m_tokens: 15.0 },
    // Gemini - current generation (Flash prices double on 01/01/27)
    { model_id: 'gemini-3.8-flash', input_cost_per_1m_tokens: 0.75, output_cost_per_1m_tokens: 3.75 },
    { model_id: 'gemini-3.6-flash', input_cost_per_1m_tokens: 0.75, output_cost_per_1m_tokens: 3.75 },
    { model_id: 'gemini-3.5-flash-lite', input_cost_per_1m_tokens: 0.30, output_cost_per_1m_tokens: 2.50 },
  ];

  const [modelCosts, setModelCosts] = useState(DEFAULT_MODEL_COSTS);

  // Saved rates win for models they cover; defaults fill in everything else.
  const mergeModelCosts = (saved) => {
    const byId = new Map(DEFAULT_MODEL_COSTS.map(c => [c.model_id, c]));
    (saved || []).forEach(c => { if (c && c.model_id) byId.set(c.model_id, { ...byId.get(c.model_id), ...c }); });
    return Array.from(byId.values());
  };

  // All available models organized by provider. Claude Sonnet 4, Claude 3.5
  // Haiku and Gemini 2.0 Flash were retired by their vendors in 2026.
  const ALL_MODELS = {
    gemini: [
      { value: 'gemini-3.8-flash', label: 'Gemini 3.8 Flash', description: 'Current Gemini Flash; price doubles 01/01/27', cost: '$0.75 / $3.75 per 1M tokens' },
      { value: 'gemini-3.6-flash', label: 'Gemini 3.6 Flash', description: "Google's replacement for Gemini 2.0 Flash", cost: '$0.75 / $3.75 per 1M tokens' },
      { value: 'gemini-3.5-flash-lite', label: 'Gemini 3.5 Flash-Lite', description: 'Cheapest current Gemini', cost: '$0.30 / $2.50 per 1M tokens' },
    ],
    claude_aliases: [
      { value: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5', description: 'Best extraction accuracy per dollar; replaces the retired Sonnet 4', cost: '$2.00 / $10.00 per 1M tokens' },
      { value: 'claude-haiku-5-5', label: 'Claude Haiku 5.5', description: 'Cheapest Claude; classification, normalization and smart input', cost: '$0.10 / $0.50 per 1M tokens' },
      { value: 'claude-opus-5-5', label: 'Claude Opus 5.5', description: 'Highest accuracy, slower and 2x the cost of Sonnet', cost: '$4.00 / $20.00 per 1M tokens' },
    ],
    claude_pinned: [
      { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 (Oct 2025, legacy)', description: 'Still served; migrate to Haiku 5.5', cost: '$1.00 / $5.00 per 1M tokens' },
      { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (legacy)', description: 'Previous generation Sonnet', cost: '$3.00 / $15.00 per 1M tokens' },
    ]
  };

  // Flat list for convenience
  const ALL_MODELS_FLAT = [...ALL_MODELS.gemini, ...ALL_MODELS.claude_aliases, ...ALL_MODELS.claude_pinned];

  // Get provider from model name
  const getProviderFromModel = (modelId) => {
    if (modelId?.startsWith('claude')) return 'claude';
    if (modelId?.startsWith('gemini')) return 'gemini';
    return 'unknown';
  };

  useEffect(() => {
    if (user?.role === 'superadmin') {
      loadSettings();
    }
  }, [user]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(`${apiBase}/admin/system-settings`, { headers });
      setSettings(response.data);
      if (response.data.parsing_feedback) {
        setParsingFeedback(response.data.parsing_feedback);
      }
      if (response.data.ai_settings) {
        // Merge loaded settings with defaults to preserve any missing fields
        setAiSettings(prev => ({
          ...prev,
          ...response.data.ai_settings,
          parsing_recovery: recoveryPolicy(response.data.ai_settings.parsing_recovery)
        }));
        // Load model costs if available. Saved lists predate newer models, so
        // overlay them on the defaults instead of replacing the defaults;
        // otherwise new models show $0 and cannot be edited.
        if (response.data.ai_settings.model_costs) {
          setModelCosts(mergeModelCosts(response.data.ai_settings.model_costs));
        }
      }
    } catch (err) {
      console.error('Error loading settings:', err);
      setError('Failed to load settings: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (recoveryError(aiSettings.parsing_recovery)) return;
    setSaving(true);
    setError('');
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const response = await axios.put(
        `${apiBase}/admin/system-settings`,
        {
          parsing_feedback: parsingFeedback,
          ai_settings: {
            ...aiSettings,
            parsing_recovery: serializeRecovery(aiSettings.parsing_recovery),
            model_costs: modelCosts
          }
        },
        { headers }
      );

      setSettings(response.data);
      setAiSettings(prev => ({ ...prev, ...response.data.ai_settings,
        parsing_recovery: recoveryPolicy(response.data.ai_settings?.parsing_recovery) }));
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save settings: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const toggleParsingFeedback = (key) => {
    setParsingFeedback(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const updateModelCost = (modelId, field, value) => {
    setModelCosts(prev => {
      const exists = prev.some(cost => cost.model_id === modelId);
      const base = exists ? prev : [...prev, getModelCost(modelId)];
      return base.map(cost =>
        cost.model_id === modelId
          ? { ...cost, [field]: parseFloat(value) || 0 }
          : cost
      );
    });
  };

  const getModelCost = (modelId) => {
    return modelCosts.find(c => c.model_id === modelId) || {
      model_id: modelId,
      input_cost_per_1m_tokens: 0,
      output_cost_per_1m_tokens: 0
    };
  };

  // Check if model costs have changed
  const modelCostsChanged = () => {
    const savedCosts = settings?.ai_settings?.model_costs || DEFAULT_MODEL_COSTS;
    return modelCosts.some(cost => {
      const savedCost = savedCosts.find(c => c.model_id === cost.model_id);
      if (!savedCost) return true;
      return cost.input_cost_per_1m_tokens !== savedCost.input_cost_per_1m_tokens ||
             cost.output_cost_per_1m_tokens !== savedCost.output_cost_per_1m_tokens;
    });
  };

  const parsingFeedbackChanged = settings?.parsing_feedback &&
    (parsingFeedback.s3_export_enabled !== settings.parsing_feedback.s3_export_enabled ||
     parsingFeedback.github_commit_enabled !== settings.parsing_feedback.github_commit_enabled);

  const aiSettingsChanged = settings?.ai_settings &&
    (aiSettings.preprocess_model !== settings.ai_settings.preprocess_model ||
     aiSettings.parsing_model !== settings.ai_settings.parsing_model ||
     aiSettings.airline_normalization_model !== settings.ai_settings.airline_normalization_model ||
     aiSettings.smart_input_model !== settings.ai_settings.smart_input_model ||
     aiSettings.ai_model_fallback_enabled !== settings.ai_settings.ai_model_fallback_enabled ||
     aiSettings.ai_model_fallback_model !== settings.ai_settings.ai_model_fallback_model ||
     aiSettings.ai_model_retry_count !== settings.ai_settings.ai_model_retry_count ||
     aiSettings.ai_model_retry_backoff_ms !== settings.ai_settings.ai_model_retry_backoff_ms);

  // Also consider changes if settings don't exist yet
  const recoveryChanged = JSON.stringify(serializeRecovery(aiSettings.parsing_recovery)) !==
    JSON.stringify(serializeRecovery(recoveryPolicy(settings?.ai_settings?.parsing_recovery)));

  const hasChanges = recoveryChanged || parsingFeedbackChanged || aiSettingsChanged || modelCostsChanged() ||
    (!settings?.ai_settings && (
      aiSettings.preprocess_model !== 'claude-haiku-5-5' ||
      aiSettings.parsing_model !== 'claude-sonnet-5-5' ||
      aiSettings.airline_normalization_model !== 'claude-haiku-5-5' ||
      aiSettings.smart_input_model !== 'claude-haiku-5-5'
    ));

  if (user?.role !== 'superadmin') {
    return (
      <div className="p-6">
        <div className="text-center text-red-600">
          Access denied. Superadmin privileges required.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Model selector component
  const ModelSelector = ({ value, onChange, label, description, icon, recommended }) => {
    const currentProvider = getProviderFromModel(value);

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-2xl">{icon}</span>
          <div>
            <h4 className="text-sm font-medium text-gray-900">{label}</h4>
            <p className="text-sm text-gray-500">{description}</p>
          </div>
        </div>

        {/* Google/Gemini Models */}
        <div className="ml-9">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">🔷</span>
            <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Google Gemini</span>
          </div>
          <div className="space-y-2">
            {ALL_MODELS.gemini.map((model) => (
              <label
                key={model.value}
                className={`flex items-start p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                  value === model.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name={label.replace(/\s/g, '_')}
                  value={model.value}
                  checked={value === model.value}
                  onChange={(e) => onChange(e.target.value)}
                  className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                />
                <div className="ml-3 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{model.label}</span>
                    {recommended === model.value && (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Recommended</span>
                    )}
                  </div>
                  <span className="block text-sm text-gray-500">{model.description}</span>
                  <span className="block text-xs text-gray-400 mt-1">{model.cost}</span>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Claude/Anthropic Models - Current */}
        <div className="ml-9 mt-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">🟠</span>
            <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Anthropic Claude — Current models</span>
          </div>
          <div className="space-y-2">
            {ALL_MODELS.claude_aliases.map((model) => (
              <label
                key={model.value}
                className={`flex items-start p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                  value === model.value
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name={label.replace(/\s/g, '_')}
                  value={model.value}
                  checked={value === model.value}
                  onChange={(e) => onChange(e.target.value)}
                  className="mt-1 h-4 w-4 text-orange-600 focus:ring-orange-500"
                />
                <div className="ml-3 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{model.label}</span>
                    {recommended === model.value && (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Recommended</span>
                    )}
                  </div>
                  <span className="block text-sm text-gray-500">{model.description}</span>
                  <span className="block text-xs text-gray-400 mt-1">{model.cost}</span>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Claude/Anthropic Models - Legacy */}
        <div className="ml-9 mt-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">📌</span>
            <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Anthropic Claude — Legacy</span>
          </div>
          <div className="space-y-2">
            {ALL_MODELS.claude_pinned.map((model) => (
              <label
                key={model.value}
                className={`flex items-start p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                  value === model.value
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name={label.replace(/\s/g, '_')}
                  value={model.value}
                  checked={value === model.value}
                  onChange={(e) => onChange(e.target.value)}
                  className="mt-1 h-4 w-4 text-orange-600 focus:ring-orange-500"
                />
                <div className="ml-3 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{model.label}</span>
                    {recommended === model.value && (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Recommended</span>
                    )}
                  </div>
                  <span className="block text-sm text-gray-500">{model.description}</span>
                  <span className="block text-xs text-gray-400 mt-1">{model.cost}</span>
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Settings</h1>
          <p className="text-gray-600">Configure global system behavior</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges || Boolean(recoveryError(aiSettings.parsing_recovery))}
          className={`px-4 py-2 rounded-md font-medium flex items-center gap-2 ${
            hasChanges
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
          }`}
        >
          {saving ? (
            <>
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* AI Model Settings */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-medium text-gray-900">AI Model Configuration</h3>
          <p className="text-sm text-gray-500 mt-1">Configure which AI models are used for document processing. Changes take effect after saving.</p>
        </div>
        <div className="p-6 space-y-8">

          {/* Current Configuration Summary */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Selected Configuration</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2">
                <span className="text-lg">⚡</span>
                <div>
                  <span className="text-xs text-gray-500 block">Pre-processing</span>
                  <span className="text-sm font-medium text-gray-900">
                    {ALL_MODELS_FLAT.find(m => m.value === aiSettings.preprocess_model)?.label || aiSettings.preprocess_model}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg">🎯</span>
                <div>
                  <span className="text-xs text-gray-500 block">{aiSettings.parsing_recovery.enabled ? 'Parsing recovery' : 'Standard parsing'}</span>
                  <span className="text-sm font-medium text-gray-900">
                    {aiSettings.parsing_recovery.enabled ? recoveryModeLabel(aiSettings.parsing_recovery) : ALL_MODELS_FLAT.find(m => m.value === aiSettings.parsing_model)?.label || aiSettings.parsing_model}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg">✈️</span>
                <div>
                  <span className="text-xs text-gray-500 block">Airline Lookup</span>
                  <span className="text-sm font-medium text-gray-900">
                    {ALL_MODELS_FLAT.find(m => m.value === aiSettings.airline_normalization_model)?.label || aiSettings.airline_normalization_model}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg">📝</span>
                <div>
                  <span className="text-xs text-gray-500 block">Smart Input</span>
                  <span className="text-sm font-medium text-gray-900">
                    {ALL_MODELS_FLAT.find(m => m.value === aiSettings.smart_input_model)?.label || aiSettings.smart_input_model}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg">🔄</span>
                <div>
                  <span className="text-xs text-gray-500 block">Standard fallback</span>
                  <span className="text-sm font-medium text-gray-900">
                    {aiSettings.ai_model_fallback_enabled
                      ? ALL_MODELS_FLAT.find(m => m.value === aiSettings.ai_model_fallback_model)?.label || aiSettings.ai_model_fallback_model
                      : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Pre-process Model Selection */}
          <ModelSelector
            value={aiSettings.preprocess_model}
            onChange={(value) => setAiSettings({ ...aiSettings, preprocess_model: value })}
            label="Pre-processing Model"
            description="Used for email segment classification and attachment classification. Choose a fast, cost-effective model."
            icon="⚡"
            recommended="claude-haiku-5-5"
          />

          {/* Parsing Model Selection */}
          <div className="pt-6 border-t border-gray-200">
            <ModelSelector
              value={aiSettings.parsing_model}
              onChange={(value) => setAiSettings({ ...aiSettings, parsing_model: value })}
              label="Standard Parsing Model"
              description="Used for travel document extraction when AI parsing recovery is off."
              icon="🎯"
              recommended="claude-sonnet-5-5"
            />
          </div>

          <ParsingRecoverySettings
            value={aiSettings.parsing_recovery}
            onChange={parsing_recovery => setAiSettings(prev => ({ ...prev, parsing_recovery }))}
            saving={saving}
          />

          {/* Airline Normalization Model Selection */}
          <div className="pt-6 border-t border-gray-200">
            <ModelSelector
              value={aiSettings.airline_normalization_model}
              onChange={(value) => setAiSettings({ ...aiSettings, airline_normalization_model: value })}
              label="Airline Normalization Model"
              description="Used as fallback when airline name cannot be matched deterministically. Fast, cheap model recommended since it handles simple lookups."
              icon="✈️"
              recommended="claude-haiku-5-5"
            />
          </div>

          {/* Smart Input Model Selection */}
          <div className="pt-6 border-t border-gray-200">
            <ModelSelector
              value={aiSettings.smart_input_model}
              onChange={(value) => setAiSettings({ ...aiSettings, smart_input_model: value })}
              label="Smart Input Model (Parsing Review)"
              description="Used for AI-powered smart input parsing in the review queue. Handles simple field extraction from pasted text. A fast, cheap model is recommended."
              icon="📝"
              recommended="claude-haiku-5-5"
            />
          </div>

          {/* Fallback Configuration */}
          <div className="pt-6 border-t border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl">🔄</span>
              <div>
                <h4 className="text-sm font-medium text-gray-900">Standard AI Model Fallback Configuration</h4>
                <p className="text-sm text-gray-500">Configure retry behavior when primary parsing model fails (truncation, network errors, etc.)</p>
              </div>
            </div>

            <div className="ml-9 space-y-6">
              {/* Enable Fallback Toggle */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h5 className="text-sm font-medium text-gray-900">Enable Model Fallback</h5>
                  <p className="text-sm text-gray-500">When enabled, retries with a different model if primary model fails or truncates</p>
                </div>
                <button
                  onClick={() => setAiSettings({ ...aiSettings, ai_model_fallback_enabled: !aiSettings.ai_model_fallback_enabled })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    aiSettings.ai_model_fallback_enabled ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      aiSettings.ai_model_fallback_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Fallback Model Selection - only show if enabled */}
              {aiSettings.ai_model_fallback_enabled && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fallback Model</label>
                    <p className="text-sm text-gray-500 mb-3">Model to use when primary parsing model fails</p>
                    <select
                      value={aiSettings.ai_model_fallback_model}
                      onChange={(e) => setAiSettings({ ...aiSettings, ai_model_fallback_model: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <optgroup label="Claude Aliases (auto-update)">
                        {ALL_MODELS.claude_aliases.map((model) => (
                          <option key={model.value} value={model.value}>
                            {model.label} - {model.description}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Claude Pinned Versions">
                        {ALL_MODELS.claude_pinned.map((model) => (
                          <option key={model.value} value={model.value}>
                            {model.label} - {model.description}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Gemini (Google)">
                        {ALL_MODELS.gemini.map((model) => (
                          <option key={model.value} value={model.value}>
                            {model.label} - {model.description}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </div>

                  {/* Retry Settings */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Retry Count</label>
                      <p className="text-xs text-gray-500 mb-2">Number of retries for transient errors (0-5)</p>
                      <input
                        type="number"
                        min="0"
                        max="5"
                        value={aiSettings.ai_model_retry_count}
                        onChange={(e) => setAiSettings({ ...aiSettings, ai_model_retry_count: Math.min(5, Math.max(0, parseInt(e.target.value) || 0)) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Initial Backoff (ms)</label>
                      <p className="text-xs text-gray-500 mb-2">Delay before first retry (500-5000ms)</p>
                      <input
                        type="number"
                        min="500"
                        max="5000"
                        step="100"
                        value={aiSettings.ai_model_retry_backoff_ms}
                        onChange={(e) => setAiSettings({ ...aiSettings, ai_model_retry_backoff_ms: Math.min(5000, Math.max(500, parseInt(e.target.value) || 1000)) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* Retry behavior explanation */}
                  <div className="p-3 bg-gray-50 rounded-md text-sm text-gray-600">
                    <p className="font-medium mb-1">Retry behavior:</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      <li>Transient errors (rate limits, timeouts) will retry up to <strong>{aiSettings.ai_model_retry_count}</strong> times with exponential backoff</li>
                      <li>Backoff sequence: {aiSettings.ai_model_retry_backoff_ms}ms → {aiSettings.ai_model_retry_backoff_ms * 2}ms → {aiSettings.ai_model_retry_backoff_ms * 4}ms</li>
                      <li>If primary model truncates or JSON parsing fails, will retry once with <strong>{ALL_MODELS_FLAT.find(m => m.value === aiSettings.ai_model_fallback_model)?.label || aiSettings.ai_model_fallback_model}</strong></li>
                    </ul>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Info box */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-start gap-2 text-blue-800">
              <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm">
                <p className="font-medium mb-1">How it works:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-700">
                  <li><strong>Pre-processing</strong> classifies email segments and filters non-travel content before parsing</li>
                  <li><strong>Parsing</strong> extracts structured data (dates, confirmations, etc.) from travel documents</li>
                  <li><strong>Airline Normalization</strong> resolves airline names to IATA codes when deterministic matching fails (e.g., typos, unusual names)</li>
                  <li>Using cheap/fast models for pre-processing and airline lookup optimizes cost, while accurate models for parsing ensures quality</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Model Cost Configuration */}
          <div className="pt-6 border-t border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">💰</span>
              <h4 className="text-sm font-medium text-gray-900">Model Cost Configuration (USD per 1M tokens)</h4>
            </div>
            <p className="text-sm text-gray-500 mb-4 ml-9">
              Configure the cost per 1 million tokens for each model. Used for AI usage cost estimation.
            </p>

            <div className="ml-9 space-y-4">
              {/* Claude Models Costs */}
              <div className="bg-orange-50 p-4 rounded-lg">
                <h5 className="text-sm font-medium text-orange-800 mb-3">Claude Models (Anthropic)</h5>
                <div className="space-y-3">
                  {[...ALL_MODELS.claude_aliases, ...ALL_MODELS.claude_pinned].map((model) => {
                    const cost = getModelCost(model.value);
                    return (
                      <div key={model.value} className="flex items-center gap-4">
                        <span className="text-sm text-gray-700 w-48">{model.label}</span>
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-500">Input:</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={cost.input_cost_per_1m_tokens}
                            onChange={(e) => updateModelCost(model.value, 'input_cost_per_1m_tokens', e.target.value)}
                            className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-orange-500 focus:border-orange-500"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-500">Output:</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={cost.output_cost_per_1m_tokens}
                            onChange={(e) => updateModelCost(model.value, 'output_cost_per_1m_tokens', e.target.value)}
                            className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-orange-500 focus:border-orange-500"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Gemini Models Costs */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h5 className="text-sm font-medium text-blue-800 mb-3">Gemini Models (Google)</h5>
                <div className="space-y-3">
                  {ALL_MODELS.gemini.map((model) => {
                    const cost = getModelCost(model.value);
                    return (
                      <div key={model.value} className="flex items-center gap-4">
                        <span className="text-sm text-gray-700 w-48">{model.label}</span>
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-500">Input:</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={cost.input_cost_per_1m_tokens}
                            onChange={(e) => updateModelCost(model.value, 'input_cost_per_1m_tokens', e.target.value)}
                            className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-500">Output:</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={cost.output_cost_per_1m_tokens}
                            onChange={(e) => updateModelCost(model.value, 'output_cost_per_1m_tokens', e.target.value)}
                            className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Parsing Feedback Export Settings */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-medium text-gray-900">Parsing Feedback Export</h3>
          <p className="text-sm text-gray-500 mt-1">Configure how parsing feedback is exported for Claude Code analysis</p>
        </div>
        <div className="p-6 space-y-6">
          {/* S3 Export Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-2xl">☁️</span>
                <h4 className="text-sm font-medium text-gray-900">S3 Export (Presigned URLs)</h4>
              </div>
              <p className="text-sm text-gray-500 mt-1 ml-9">
                Export feedback with S3 presigned URLs for document access. URLs expire after 24 hours.
              </p>
            </div>
            <button
              onClick={() => toggleParsingFeedback('s3_export_enabled')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                parsingFeedback.s3_export_enabled ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  parsingFeedback.s3_export_enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* GitHub Commit Toggle */}
          <div className="flex items-center justify-between border-t border-gray-100 pt-6">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-2xl">📦</span>
                <h4 className="text-sm font-medium text-gray-900">GitHub Commit (Send to Claude Code)</h4>
              </div>
              <p className="text-sm text-gray-500 mt-1 ml-9">
                Commit feedback documents directly to GitHub repository for Claude Code access. Recommended for Claude Code workflows.
              </p>
            </div>
            <button
              onClick={() => toggleParsingFeedback('github_commit_enabled')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                parsingFeedback.github_commit_enabled ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  parsingFeedback.github_commit_enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Warning if both are disabled */}
          {!parsingFeedback.s3_export_enabled && !parsingFeedback.github_commit_enabled && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="flex items-center gap-2 text-yellow-800">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span className="font-medium">Warning:</span>
                <span>Both export methods are disabled. You won't be able to export parsing feedback.</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Last Updated Info */}
      {settings?.updated_at && (
        <div className="text-sm text-gray-500">
          Last updated: {new Date(settings.updated_at).toLocaleString()}
          {settings.updated_by_user_email && ` by ${settings.updated_by_user_email}`}
        </div>
      )}
    </div>
  );
};

export default SuperadminSettings;
